/* oxlint-disable no-console */
import { InitConfiguration, type Context, type User } from '@datadog/browser-core';
import { LogsGlobal } from '@datadog/browser-logs';

import { isBuild, isServer } from './env-helper';
import { removeLocalePathname } from './pathname';

interface InitOptions extends Omit<InitConfiguration, 'service' | 'beforeSend'>, Record<string, unknown> {
  env: 'prod' | 'stg' | 'dev' | 'sandbox';
  service: 'prex-frontend-nextjs' | 'prex-webview';
  applicationId: string;
  // oxlint-disable-next-line no-explicit-any
  beforeSend?: (event: any, context: any) => boolean;
}

// copy from @datadog/browser-rum and @datadog/browser-rum-slim.
// useful type does not exists
export interface RUMGlobal<T extends InitOptions> {
  init: (config: T) => void;

  setViewName: (name: string) => void;

  setViewContext: (context: Context) => void;

  setViewContextProperty: (key: string, value: unknown) => void;

  getViewContext: () => Context;

  setGlobalContext: (context: Context) => void;

  getGlobalContext: () => Context;

  setGlobalContextProperty: (key: unknown, value: unknown) => void;

  removeGlobalContextProperty: (key: unknown) => void;

  clearGlobalContext: () => void;

  addAction: (name: string, context?: object) => void;

  addError: (error: unknown, context?: object) => void;

  addTiming: (name: string, time?: number) => void;

  setUser(newUser: User & { id: string }): void;

  getUser: () => Context;

  setUserProperty: (key: unknown, property: unknown) => void;

  removeUserProperty: (key: unknown) => void;

  clearUser: () => void;
}

let isInitDatadog = false;
let buildEnv: InitOptions['env'];
let rumGlobal: RUMGlobal<InitOptions>;
let logsGlobal: LogsGlobal;

export function datadogInit<T extends InitOptions>(rum: RUMGlobal<T>, logs: LogsGlobal, options: T) {
  if (isInitDatadog || isServer) return;

  rumGlobal = rum as RUMGlobal<InitOptions>;
  logsGlobal = logs;
  isInitDatadog = true;
  buildEnv = options.env;

  rum.init({
    site: 'datadoghq.com',
    sessionSampleRate: 100,
    trackUserInteractions: true,
    trackResources: true,
    trackLongTasks: true,
    defaultPrivacyLevel: 'mask-user-input',
    allowedTracingUrls: [
      (urlString: string) => {
        const url = new URL(urlString);
        /**
         * https://prextech.slack.com/archives/C05BSR5LQUX/p1716976971212569
         * klines request should be simple request. when allow tracing, datadog injects custom header
         */
        if (url.pathname.startsWith('/api/v2/chart/klines/')) return false;
        return ['api.flipster.io', 'api.flipster.me', 'api.stg.prex.run'].includes(url.hostname);
      },
    ],
    ...options,
    beforeSend(event, context) {
      if (event.view && !event.view.name) {
        const url = new URL(event.view.url);
        // oxlint-disable-next-line no-param-reassign
        event.view.name = removeLocalePathname(url.pathname);
      }
      if (event.type === 'resource' && 'resource' in event) {
        const url = new URL(event.resource.url);
        return ['api.segment.io', 'in.ap1.segmentapis.com', 'www.google-analytics.com'].every(
          (blockedHost) => url.hostname !== blockedHost,
        );
      }
      return options.beforeSend?.(event, context) ?? true;
    },
  });

  logs.init({
    site: 'datadoghq.com',
    sessionSampleRate: 100,
    forwardErrorsToLogs: true,
    forwardConsoleLogs: ['error', 'warn'],
    clientToken: options.clientToken,
    env: options.env,
    service: options.service,
    version: options.version,
  });
}

export const datadog = {
  setUser(user: { id: string; email: string } | null) {
    if (!isInitDatadog) return;
    if (user) {
      rumGlobal.setUser(user);
      logsGlobal.setUser(user);
    } else {
      rumGlobal.clearUser();
      logsGlobal.clearUser();
    }
  },
  setContext(key: string, value: string | number | boolean) {
    if (!isInitDatadog) return;
    rumGlobal.setGlobalContextProperty(key, value);
    logsGlobal.setGlobalContextProperty(key, value);
  },
  setAppContext(context: Record<string, string | number | boolean>) {
    if (!isInitDatadog) return;
    if (Object.values(context).every((value) => !value)) return;
    rumGlobal.setGlobalContext(context);
    logsGlobal.setGlobalContext(context);
  },
  removeContext(key: string) {
    if (!isInitDatadog) return;
    rumGlobal.removeGlobalContextProperty(key);
    logsGlobal.removeGlobalContextProperty(key);
  },
  addAction(name: string, context: Record<string, string | number>) {
    if (!isInitDatadog) return;
    rumGlobal.addAction(name, context);
  },
  addTiming(name: string) {
    if (!isInitDatadog) return;
    rumGlobal.addTiming(name);
  },
  addError(error: unknown, context?: Record<string, unknown>) {
    if (!isInitDatadog) return;
    const normalizedError = normalizeLogError(error);
    rumGlobal.addError(normalizedError, context);
    logsGlobal.logger.error(normalizedError?.message ?? 'Unknown Error', context, normalizedError);
  },
};

function normalizeLogError(error: unknown) {
  if (error instanceof Error) return error;
  if (error != null) return new Error(String(error));
  return undefined;
}

function createLogger(name: string, context: Record<string, unknown>) {
  const shouldLogToConsole = () => {
    if (!isBuild) return true;
    return !isServer && ['stg', 'sandbox', 'dev'].includes(buildEnv);
  };

  return {
    info: (message: string, extraContext?: Record<string, unknown>) => {
      if (shouldLogToConsole()) {
        console.log(`[Datadog - ${name}]`, { message, ...extraContext });
      }
      if (!logsGlobal) return;
      const logger = logsGlobal.createLogger(name, { context });
      logger.info(message, extraContext);
    },
    warn: (message: string, extraContext?: Record<string, unknown>, error?: unknown) => {
      if (shouldLogToConsole()) {
        console.warn(`[Datadog - ${name}]`, { message, ...extraContext });
      }
      if (!logsGlobal) return;
      const logger = logsGlobal.createLogger(name, { context });
      logger.warn(message, extraContext, normalizeLogError(error));
    },
    error: (message: string, extraContext?: Record<string, unknown>, error?: unknown) => {
      if (shouldLogToConsole()) {
        console.error(`[Datadog - ${name}]`, { message, error, ...extraContext });
      }
      if (!logsGlobal || !rumGlobal) return;
      const logger = logsGlobal.createLogger(name, { context });
      logger.error(message, extraContext, normalizeLogError(error));
      rumGlobal.addError(normalizeLogError(error) ?? extraContext?.error_message ?? message, {
        ...context,
        message,
      });
    },
  };
}

export const tradeLogger = createLogger('trade', {
  domain: 'trade',
  log_version: 1,
});
